import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { Clock } from 'lucide-react';
import * as PapeletaService from '../../axios_services/papeletasalida.service';

const MOTIVOS = [
  { value: 'PERSONAL',     label: 'Personal' },
  { value: 'MEDICO',       label: 'Médico / Salud' },
  { value: 'COMISION',     label: 'Comisión de servicio' },
  { value: 'TRAMITES',     label: 'Trámites personales' },
  { value: 'CAPACITACION', label: 'Capacitación' },
  { value: 'OTROS',        label: 'Otros' },
];

const HOURS   = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const PERIODS = ['AM', 'PM'];

const to24h = (h, m, p) => {
  let hr = parseInt(h, 10);
  if (p === 'PM' && hr < 12) hr += 12;
  if (p === 'AM' && hr === 12) hr = 0;
  return `${String(hr).padStart(2, '0')}:${m}`;
};

const ModalPapeletaSalida = ({ isOpen, onClose, onRefresh, currentUserId }) => {
  const today = new Date().toISOString().split('T')[0];

  const getInitial = () => ({
    fecha_salida:   today,
    hora_salida:    '',
    hora_regreso:   '',
    tiene_regreso:  false,
    motivo:         'PERSONAL',
    motivo_detalle: '',
    es_remunerado:  'false',
  });

  const [formData, setFormData] = useState(getInitial());
  const [saving,   setSaving]   = useState(false);

  const [tSalida,  setTSalida]  = useState({ h: '08', m: '00', p: 'AM' });
  const [tRegreso, setTRegreso] = useState({ h: '02', m: '00', p: 'PM' });

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitial());
      setTSalida({ h: '08', m: '00', p: 'AM' });
      setTRegreso({ h: '02', m: '00', p: 'PM' });
    }
  }, [isOpen]);

  // Sync 12h parts to 24h formData
  useEffect(() => {
    setFormData(prev => ({ ...prev, hora_salida: to24h(tSalida.h, tSalida.m, tSalida.p) }));
  }, [tSalida]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, hora_regreso: to24h(tRegreso.h, tRegreso.m, tRegreso.p) }));
  }, [tRegreso]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async () => {
    if (!formData.hora_salida) {
      alert('La Hora de Salida es obligatoria.');
      return;
    }
    if (formData.motivo === 'OTROS' && !formData.motivo_detalle.trim()) {
      alert('Especifique el detalle del motivo.');
      return;
    }
    if (!window.confirm('¿Desea registrar esta papeleta de salida?')) return;
    await doRegistrar();
  };

  const doRegistrar = async () => {
    setSaving(true);
    try {
      await PapeletaService.insertPapeleta({
        usuario_id:     currentUserId,
        fecha_salida:   formData.fecha_salida,
        hora_salida:    formData.hora_salida,
        hora_regreso:   formData.tiene_regreso ? formData.hora_regreso || null : null,
        motivo:         formData.motivo,
        motivo_detalle: formData.motivo === 'OTROS' ? formData.motivo_detalle : null,
        es_remunerado:  formData.es_remunerado === 'true',
      });
      onRefresh();
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al registrar la papeleta.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} className="custom-modal" backdrop="static">
      <ModalHeader className="modal-header">
        <div className="titulo">
          <h1>NUEVA PAPELETA DE SALIDA</h1>
          <Button className="close-btn" onClick={onClose}>X</Button>
        </div>
      </ModalHeader>
      <div className="modal-body-footer">
        <ModalBody className="modal-body">
          <Form className="fila" onSubmit={e => e.preventDefault()}>
            <FormGroup style={{ gridColumn: '1 / -1' }}>
              <Label>Motivo: *</Label>
              <Input
                type="select"
                name="motivo"
                value={formData.motivo}
                onChange={handleChange}
              >
                {MOTIVOS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Fecha de salida: *</Label>
              <Input
                type="date"
                name="fecha_salida"
                value={formData.fecha_salida}
                onChange={handleChange}
              />
            </FormGroup>

            <FormGroup>
                <Label>Hora de salida: *</Label>
                <div className="d-flex gap-1">
                  <Input type="select" value={tSalida.h} onChange={e => setTSalida(prev => ({ ...prev, h: e.target.value }))}>
                    {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                  </Input>
                  <Input type="select" value={tSalida.m} onChange={e => setTSalida(prev => ({ ...prev, m: e.target.value }))}>
                    {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
                  </Input>
                  <Input type="select" value={tSalida.p} onChange={e => setTSalida(prev => ({ ...prev, p: e.target.value }))}>
                    {PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
                  </Input>
                </div>
              </FormGroup>

            <FormGroup check className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: 'auto' }}>
                <Input
                  type="checkbox"
                  name="tiene_regreso"
                  checked={formData.tiene_regreso}
                  onChange={handleChange}
                  id="tiene_regreso"
                  style={{ margin: 0, marginTop: '-2px', accentColor: '#0549a7' }}
                />
                <Label check htmlFor="tiene_regreso" style={{ margin: 0 }}>
                  Indicar hora de regreso
                </Label>
              </FormGroup>

            {formData.tiene_regreso && (
              <FormGroup>
                <Label>Hora de regreso:</Label>
                <div className="d-flex gap-1">
                  <Input type="select" value={tRegreso.h} onChange={e => setTRegreso(prev => ({ ...prev, h: e.target.value }))}>
                    {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                  </Input>
                  <Input type="select" value={tRegreso.m} onChange={e => setTRegreso(prev => ({ ...prev, m: e.target.value }))}>
                    {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
                  </Input>
                  <Input type="select" value={tRegreso.p} onChange={e => setTRegreso(prev => ({ ...prev, p: e.target.value }))}>
                    {PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
                  </Input>
                </div>
              </FormGroup>
            )}

            {formData.motivo === 'OTROS' && (
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>Detalles: *</Label>
                <Input
                  type="textarea"
                  name="motivo_detalle"
                  value={formData.motivo_detalle}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Describa el motivo..."
                />
              </FormGroup>
            )}



            <FormGroup style={{ marginTop: '18px' }}>
              <div className="button-container">
                <Button
                  className="button-custom"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                        {saving ? 'REGISTRANDO...' : 'REGISTRAR'}
                </Button>
              </div>
            </FormGroup>
          </Form>
        </ModalBody>
      </div>
    </Modal>
  );
};

export default ModalPapeletaSalida;
